import { type VariantProps, cva } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { Slot } from 'radix-ui';
import * as React from 'react';
import { cn } from '../../lib/utils';

/**
 * Button — Generated from Figma Nimbus v3 Design System
 * @figma-node 26114:652
 * @figma-url https://www.figma.com/design/euN3FdxtNlCC8GiKC66YmG/Nimbus-v3--TBD-?node-id=26114:652
 *
 * Variants: Default, Secondary, Destructive, Outline, Ghost, Link, Accent
 * Sizes: default, sm, lg, xl, icon, icon-sm, icon-lg
 * States: Default, Hover, Focus, Loading, Disabled, Pressed
 *
 * All colors extracted from Figma API — not guessed.
 */

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none disabled:pointer-events-none",
  {
    variants: {
      variant: {
        /*
         * Default — Figma source: pressed-states-raw.json → Default.*
         * default:  fill=#1e5eed (blue/700) text=#f9f9f9 (neutral/50)
         * hover:    fill=#ebf2ff (blue/100) text=#1e5eed
         * focus:    ring 3px #a3a3a3/50, border=#1e5eed
         * disabled: fill=#727272 (muted-foreground) text=#f9f9f9, opacity=0.5
         * pressed:  same fill+text, opacity=0.6 (opacity/opacity-60)
         * dark:     accent-foreground=#ebf2ff, accent=#1e5eed, muted-foreground=#d8d8d8
         */
        default:
          'rounded-full bg-nimbus-accent-foreground text-nimbus-primary-foreground hover:bg-nimbus-secondary hover:text-nimbus-accent-foreground focus-visible:ring-[3px] focus-visible:ring-nimbus-ring-50 focus-visible:border-nimbus-focus active:bg-nimbus-accent-foreground active:text-nimbus-primary-foreground active:opacity-60 disabled:bg-nimbus-muted-foreground disabled:text-nimbus-primary-foreground disabled:opacity-50',

        /*
         * Secondary — Figma source: pressed-states-raw.json → Secondary.*
         * default:  fill=transparent text=#1e5eed (blue/700)
         * hover:    fill=#ebf2ff (blue/100) text=#1e5eed
         * focus:    fill=#ebf2ff, ring 3px #a3a3a3/50, border=#1e5eed
         * disabled: fill=#efefef (muted) text=#202020 (foreground), opacity=0.5
         * pressed:  fill=#ebf2ff (blue/100), opacity=0.6 (opacity/opacity-60)
         * dark:     accent-foreground=#ebf2ff, muted=#5c5c5c, foreground=#f9f9f9
         */
        secondary:
          'rounded-full bg-transparent text-nimbus-accent-foreground hover:bg-nimbus-secondary focus-visible:bg-nimbus-secondary focus-visible:ring-[3px] focus-visible:ring-nimbus-ring-50 focus-visible:border-nimbus-focus active:bg-nimbus-secondary active:text-nimbus-accent-foreground active:opacity-60 disabled:bg-nimbus-muted disabled:text-nimbus-foreground disabled:opacity-50',

        /*
         * Destructive — Figma source: button-variant-styles.json → destructive.*
         * default:  fill=#db2929 (destructive) text=#fff8f7 (destructive-foreground)
         * hover:    same fill, same text (no visible change in Figma)
         * focus:    ring 3px #dc2626/20
         * disabled: same fill + text, opacity=0.5
         * pressed:  opacity=0.6
         * dark:     destructive=#f8a79d, destructive-foreground=#fff8f7
         */
        destructive:
          'rounded-full bg-nimbus-destructive text-nimbus-destructive-foreground hover:bg-nimbus-destructive-90 focus-visible:ring-[3px] focus-visible:ring-nimbus-destructive-20 active:bg-nimbus-destructive active:text-nimbus-destructive-foreground active:opacity-60 disabled:opacity-50',

        /*
         * Outline — Figma source: pressed-states-raw.json → Outline.*
         * default:  fill=#ffffff (base/white) text=#1e5eed stroke=#1e5eed (blue/700)
         * hover:    fill=#ebf2ff text=#1e5eed stroke=#1e5eed
         * focus:    ring 3px #a3a3a3/50, stroke=#1e5eed
         * disabled: fill=#ffffff text=#202020 (foreground) stroke=#202020, opacity=0.5
         * pressed:  fill=#ebf2ff (blue/100), opacity=0.6 (opacity/opacity-60)
         * dark:     background=#202020, foreground=#f9f9f9, accent-foreground=#ebf2ff
         */
        outline:
          'rounded-full bg-nimbus-background text-nimbus-accent-foreground border border-nimbus-accent-foreground hover:bg-nimbus-secondary focus-visible:ring-[3px] focus-visible:ring-nimbus-ring-50 active:bg-nimbus-secondary active:text-nimbus-accent-foreground active:opacity-60 disabled:text-nimbus-foreground disabled:border-nimbus-foreground disabled:opacity-50 dark:hover:bg-white/[0.075]',

        /*
         * Ghost — Figma source: button-variant-styles.json → ghost.*
         * default:  no fill, text=#202020 (foreground), radius=8
         * hover:    text=#595959 (primary)
         * focus:    stroke=#a3a3a3, radius=2, ring 3px #a3a3a3/50
         * disabled: text=#202020, opacity=0.5
         * pressed:  opacity=0.6
         * dark:     foreground=#f9f9f9, primary=#ffffff
         */
        ghost:
          'rounded-lg bg-transparent text-nimbus-foreground hover:text-nimbus-primary focus-visible:ring-[3px] focus-visible:ring-nimbus-ring-50 focus-visible:rounded-sm active:text-nimbus-foreground active:opacity-60 disabled:opacity-50 dark:hover:text-white',

        /*
         * Link — Figma source: button-variant-styles.json → link.*
         * default:  no fill, text=#202020 (foreground), underline, radius=8
         * hover:    text=#595959 (primary)
         * focus:    stroke=#a3a3a3, radius=2, ring 3px (no spread shadow in Figma)
         * disabled: text=#202020, opacity=0.5
         * pressed:  opacity=0.6
         * dark:     foreground=#f9f9f9, primary=#ffffff
         */
        link: 'rounded-lg bg-transparent text-nimbus-foreground underline underline-offset-4 hover:text-nimbus-primary focus-visible:ring-[3px] focus-visible:ring-nimbus-ring-50 focus-visible:rounded-sm focus-visible:no-underline active:text-nimbus-foreground active:opacity-60 disabled:opacity-50 dark:hover:text-white',

        /*
         * Accent — Figma source: button-variant-styles.json → accent.*
         * default:  fill=#ff9d28 (orange/700) text=#142d63 (blue/950)
         * hover:    fill=#ffd5a2 (orange/400) text=#142d63
         * focus:    ring 3px #a3a3a3/50, border=#1e5eed
         * disabled: fill=#efefef (muted) text=#202020 (foreground), opacity=0.5
         * pressed:  opacity=0.6
         * dark:     muted=#5c5c5c, foreground=#f9f9f9 (orange/blue same in dark)
         */
        accent:
          'rounded-full bg-nimbus-accent-bg text-nimbus-accent-fg hover:bg-nimbus-accent-hover focus-visible:ring-[3px] focus-visible:ring-nimbus-ring-50 focus-visible:border-nimbus-focus active:bg-nimbus-accent-bg active:text-nimbus-accent-fg active:opacity-60 disabled:bg-nimbus-muted disabled:text-nimbus-foreground disabled:opacity-50',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 px-6 has-[>svg]:px-4',
        xl: 'h-12 px-8 text-base has-[>svg]:px-6',
        icon: 'size-9',
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

const ButtonSlot = Slot.createSlot('Button');
const ButtonSlottable = Slot.createSlottable('Button');

interface ButtonProps
  extends React.ComponentProps<'button'>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  loading = false,
  disabled,
  leftIcon,
  rightIcon,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? ButtonSlot : 'button';
  const isIconOnly =
    size === 'icon' || size === 'icon-sm' || size === 'icon-lg';
  const content = loading
    ? [
        <Loader2 key="loader" className="animate-spin" />,
        !isIconOnly && (
          <ButtonSlottable key="children">{children}</ButtonSlottable>
        ),
      ]
    : [
        leftIcon && <React.Fragment key="left-icon">{leftIcon}</React.Fragment>,
        <ButtonSlottable key="children">{children}</ButtonSlottable>,
        rightIcon && (
          <React.Fragment key="right-icon">{rightIcon}</React.Fragment>
        ),
      ];

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      disabled={disabled || loading}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    >
      {content}
    </Comp>
  );
}

export { Button, buttonVariants };
export type { ButtonProps };
